import dayjs from 'dayjs';
import type { AuditionBasicsState } from './BrandAuditionBasicsStep';
import type { AuditionRole } from './BrandAuditionRolesStep';

const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const pickN = <T>(arr: T[], min: number, max: number): T[] => {
    const n = min + Math.floor(Math.random() * (max - min + 1));
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(n, arr.length));
};
const randInt = (min: number, max: number) => min + Math.floor(Math.random() * (max - min + 1));

const CREW_SKILL_OPTIONS = [
    'Camera',
    'Sound',
    'Lighting',
    'Grip',
    'Art Department',
    'Hair & Makeup',
    'Wardrobe',
    'Production',
    'Locations',
    'Transport',
];

const TECHNICAL_SKILL_OPTIONS = [
    'Editing',
    'Color Grading',
    'VFX',
    'Motion Graphics',
    'Sound Design',
    'DIT',
    'Encoding / Delivery',
];

const CAST_SKILL_OPTIONS = [
    'Acting',
    'Singing',
    'Dancing',
    'Voice Acting',
    'Modelling',
    'Presenting',
    'Comedy',
    'Stunts',
    'Improvisation',
];

function skillsForGigType(gig: string): string[] {
    switch (gig) {
        case 'crew':
            return CREW_SKILL_OPTIONS;
        case 'technical':
            return TECHNICAL_SKILL_OPTIONS;
        case 'mixed':
            return Array.from(new Set([...CAST_SKILL_OPTIONS, ...CREW_SKILL_OPTIONS, ...TECHNICAL_SKILL_OPTIONS]));
        case 'cast':
        default:
            return CAST_SKILL_OPTIONS;
    }
}

const AUDITION_TITLES = [
    'Summer commercial — lead talent',
    'Indie short film — supporting roles',
    'Brand social spot — diverse cast',
    'Voice-over for animated pilot',
    'Theatre workshop — ensemble',
];

const SYNOPSES = [
    'We are casting a warm, conversational lead for a 30s lifestyle spot. Sessions are self-tape first, with callbacks in Los Angeles.',
    'Character-driven short seeking two leads with strong improv and on-camera experience. Union and non-union welcome.',
    'Fast-turn social campaign: need energetic talent comfortable with product demo and light choreography.',
];

const SHOOT_LOCATIONS = ['Los Angeles, CA', 'Atlanta, GA', 'Toronto, ON', 'New York, NY', 'Remote / self-tape'];

/** Step 0 — Project setup (wizard page 0). */
export function autoFillAuditionStep0(): Partial<AuditionBasicsState> {
    return {
        title: pick(AUDITION_TITLES),
        synopsis: pick(SYNOPSES),
        type_of_work: pick(['commercial', 'short', 'episodic', 'vertical short form']),
        audition_type: pick(['video', 'commercial', 'film', 'voice over', 'theatre']),
        gig_type: pick(['cast', 'crew', 'technical', 'mixed']),
        union_affiliation: pick(['non-union', 'SAG-AFTRA', 'not applicable']),
    };
}

/** Step 1 — Roles + ages + skills (wizard page 1). */
export function autoFillAuditionStep1Basics(gigType: string): Partial<AuditionBasicsState> {
    const pool = skillsForGigType(gigType);
    return {
        min_age: String(randInt(18, 28)),
        max_age: String(randInt(35, 55)),
        required_skills: pickN(pool, 2, Math.min(5, pool.length)),
    };
}

function devRoleId() {
    return `dev-role-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Step 1 — sample roles (passes validation: non-empty character). */
export function autoFillAuditionRoles(): AuditionRole[] {
    const a: AuditionRole = {
        id: devRoleId(),
        character: 'Lead — Dev autofill',
        description: 'Warm, approachable on-camera presence. Comfortable with scripted dialogue and one day on set.',
        compensation: randInt(15, 40) * 100,
        currency: 'USD',
        employment_type: 'Contract',
        gig_type: 'Cast',
        compensation_type: 'Paid',
        additional_benefits: [],
        verified_thspians_only: false,
        gig_expertise: '',
        portfolio_requirement: ['Headshot', 'Video reel'],
        min_age: String(randInt(21, 30)),
        max_age: String(randInt(45, 60)),
    };
    const b: AuditionRole = {
        id: devRoleId(),
        character: 'Supporting — Dev autofill',
        description: 'Strong reactions and background work; half-day call.',
        compensation: randInt(8, 20) * 100,
        currency: 'EUR',
        employment_type: 'One-off',
        gig_type: 'Cast',
        compensation_type: 'Paid',
        additional_benefits: [],
        verified_thspians_only: false,
        gig_expertise: '',
        portfolio_requirement: ['Past work'],
        min_age: String(randInt(18, 25)),
        max_age: String(randInt(40, 55)),
    };
    return Math.random() > 0.4 ? [a] : [a, b];
}

/** Step 2 — Schedule & location (wizard page 2). */
export function autoFillAuditionStep2(): Partial<AuditionBasicsState> {
    const casting = dayjs().add(randInt(10, 25), 'day').format('YYYY-MM-DD');
    const start = dayjs().add(randInt(28, 60), 'day').format('YYYY-MM-DD');
    return {
        shoot_location: pick(SHOOT_LOCATIONS),
        casting_expiration: casting,
        job_start_date: start,
    };
}

/** Step 3 — Media & production (wizard page 3); production company filled by parent if list loaded. */
export function autoFillAuditionStep3(): Partial<AuditionBasicsState> {
    return {
        submission_report: pick(['daily digest', 'weekly digest', 'applications as they come in', 'monthly digest']),
    };
}
